import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Phone, Mail, UserPlus, Building2, Filter, Calendar,
  Users as UsersIcon, CheckCircle2, XCircle, Clock, MapPin,
  Landmark, ArrowRight, UserCheck, MessageSquare, AlertCircle, 
  BarChart3, TrendingUp, CalendarDays, ClipboardList, Home, Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiError, asItems } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";

const STATUS_BADGES = {
  NEW: "bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950/40 dark:text-blue-200 dark:border-blue-800",
  MATCHING: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800",
  ASSIGNED: "bg-purple-100 text-purple-900 border-purple-300 dark:bg-purple-950/40 dark:text-purple-200 dark:border-purple-800",
  COMPLETED: "bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800",
  CANCELLED: "bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-800",
};

const ASSIGNMENT_BADGES = {
  PROPOSED: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800",
  REJECTED: "bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-800",
  APPROVED: "bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800",
  UNASSIGNED: "bg-slate-100 text-slate-900 border-slate-300 dark:bg-slate-950/40 dark:text-slate-200 dark:border-slate-800",
};

export default function OfficeCrmLeads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [offices, setOffices] = useState([]);
  const [siteVisits, setSiteVisits] = useState([]);
  const [properties, setProperties] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState("");
  const [busy, setBusy] = useState("");
  const [activeTab, setActiveTab] = useState("visits");
  
  // Office Filter
  const isUserAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [selectedOfficeId, setSelectedOfficeId] = useState(user?.office_id || "");

  // Modal Dialogs state
  const [assignDialog, setAssignDialog] = useState({ open: false, agent: null });

  // Quick Assign Lead Form State
  const [assignLeadId, setAssignLeadId] = useState("");
  const [assignNote, setAssignNote] = useState("");

  const [assignmentLeads, setAssignmentLeads] = useState([]);
  const [assignmentError, setAssignmentError] = useState("");
  const [assignmentSearch, setAssignmentSearch] = useState("");
  const [assignmentShowAll, setAssignmentShowAll] = useState(false);
  const [assignLeadDialog, setAssignLeadDialog] = useState({ open: false, lead: null });
  const [assignAgentId, setAssignAgentId] = useState("");
  const [rejectDialog, setRejectDialog] = useState({ open: false, lead: null });
  const [rejectReason, setRejectReason] = useState("");

  const loadAssignments = async (search = assignmentSearch, showAll = assignmentShowAll) => {
    setAssignmentError("");
    try {
      const res = await api.get("/v1/admin/crm/leads/pending-assignments", {
        params: { search: search.trim() || undefined, all: showAll ? "true" : undefined }
      });
      setAssignmentLeads(asItems(res.data));
    } catch (e) {
      const message = formatApiError(e);
      setAssignmentLeads([]);
      setAssignmentError(message);
      toast.error(message);
    }
  };

  const handleApproveAssignment = async (lead) => {
    setBusy(lead.lead_id);
    try {
      await api.post(`/v1/admin/crm/leads/${lead.lead_id}/approve-assignment`);
      toast.success("Assignment approved");
      loadAssignments();
      loadData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const handleRejectAssignment = async () => {
    const lead = rejectDialog.lead;
    const reason = rejectReason.trim();
    if (!lead || !reason) return;
    setBusy(lead.lead_id);
    try {
      await api.post(`/v1/admin/crm/leads/${lead.lead_id}/reject-assignment`, { reason });
      toast.success("Assignment rejected");
      setRejectDialog({ open: false, lead: null });
      setRejectReason("");
      loadAssignments();
      loadData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const openAssignLead = (lead) => {
    setAssignAgentId(lead.proposed_agent_user_id || lead.listing_agent_id || "");
    setAssignLeadDialog({ open: true, lead });
  };

  const handleAssignLeadSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    if (!assignAgentId || !assignLeadDialog.lead) return;

    setBusy("assigning-lead");
    const leadId = assignLeadDialog.lead?.lead_id || assignLeadDialog.lead?.id;
    const selectedAgent = agents.find(a => String(a.user_id || a.id) === String(assignAgentId));
    const targetOfficeId = selectedAgent?.office_id || user?.office_id || selectedOfficeId;

    try {
      if (isUserAdmin) {
        await api.post(`/v1/office/crm/leads/${leadId}/propose-assignment`, {
          agent_user_id: assignAgentId,
          office_id: targetOfficeId
        });
        toast.success("Lead assigned successfully!");
      } else {
        await api.post(`/v1/office/crm/leads/${leadId}/propose-assignment`, {
          agent_user_id: assignAgentId,
          office_id: targetOfficeId
        });
        toast.success("Assignment proposed successfully — awaiting admin approval!");
      }
      setAssignLeadDialog({ open: false, lead: null });
      setAssignAgentId("");
      loadAssignments();
      loadData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const loadData = async () => {
    setLoading(true);
    setDataError("");
    try {
      const agentRequest = isUserAdmin
        ? api.get("/v1/admin/directory/agents", { params: { limit: 200 } })
        : api.get("/office/agents");
      const officeRequest = isUserAdmin
        ? api.get("/v1/admin/directory/offices", { params: { limit: 200 } })
        : Promise.resolve({ data: { items: [] } });
      const [lRes, aRes, oRes, sRes, pRes] = await Promise.allSettled([
        api.get(isUserAdmin ? "/v1/admin/crm/leads" : "/v1/office/crm/leads"),
        agentRequest,
        officeRequest,
        api.get("/v1/site-visits"),
        api.get(`${isUserAdmin ? "/admin/properties" : "/office/properties"}?limit=200`),
      ]);

      setLeads(lRes.status === "fulfilled" ? asItems(lRes.value?.data) : []);
      const availableAgents = aRes.status === "fulfilled" ? asItems(aRes.value?.data) : [];
      setAgents(availableAgents.filter((u) => String(u.role || "agent").toLowerCase() === "agent"));
      setOffices(isUserAdmin ? (oRes.status === "fulfilled" ? asItems(oRes.value?.data) : []) : (aRes.status === "fulfilled" && aRes.value?.data?.office ? [aRes.value.data.office] : []));
      setSiteVisits(sRes.status === "fulfilled" ? asItems(sRes.value?.data) : []);
      setProperties(pRes.status === "fulfilled" ? asItems(pRes.value?.data) : []);
    } catch (e) {
      const message = formatApiError(e);
      setDataError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadAssignments("", assignmentShowAll);
  }, [assignmentShowAll]);

  // Filter lists based on selected office
  const filteredLeads = leads.filter(l => {
    if (!selectedOfficeId) return true;
    const lOffice = l.assigned_office_id || l.office_id;
    return !lOffice || String(lOffice) === String(selectedOfficeId);
  });

  const filteredAgents = agents.filter(a => {
    if (!selectedOfficeId) return true;
    return String(a.office_id) === String(selectedOfficeId);
  });

  const filteredVisits = siteVisits.filter(v => {
    if (!selectedOfficeId) return true;
    // Find agent of this visit to check office
    const agent = agents.find(a => String(a.user_id || a.id) === String(v.agent_id));
    return agent && String(agent.office_id) === String(selectedOfficeId);
  });

  // Calculate Metrics
  const totalLeadsCount = filteredLeads.length;
  const unassignedLeadsCount = filteredLeads.filter(l => !l.assigned_agent_id).length;
  const pendingVisitsCount = filteredVisits.filter(v => v.status === "SCHEDULED").length;
  const totalAgentsCount = filteredAgents.length;

  // Handle Quick Assign Submission
  const handleQuickAssign = async (e) => {
    e.preventDefault();
    if (!assignLeadId || !assignDialog.agent) return;

    setBusy("assigning");
    const agentId = assignDialog.agent?.user_id || assignDialog.agent?.id;
    const officeId = assignDialog.agent?.office_id;

    try {
      if (isUserAdmin) {
        // Direct assignment
        await api.post(`/v1/office/crm/leads/${assignLeadId}/propose-assignment`, {
          agent_user_id: agentId,
          office_id: officeId,
          note: assignNote
        });
        toast.success("Lead assigned successfully!");
      } else {
        // Proposal assignment
        await api.post(`/v1/office/crm/leads/${assignLeadId}/propose-assignment`, {
          agent_user_id: agentId,
          office_id: officeId,
          note: assignNote
        });
        toast.success("Assignment proposed successfully — awaiting admin approval!");
      }

      setAssignDialog({ open: false, agent: null });
      setAssignLeadId("");
      setAssignNote("");
      loadData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6" data-testid="office-crm-leads-page">
      
      {/* Header Panel */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-border/40 pb-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-1.5 flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5 text-primary" /> CRM command center
          </p>
          <h1 className="font-display text-3xl font-black tracking-tight uppercase text-primary">Office CRM Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Monitor pipelines, process buyer-requested site visits, and coordinate agent assignments.</p>
        </div>

        {/* Role-Based Office Selector */}
        {isUserAdmin ? (
          <div className="flex items-center gap-2 bg-card border border-border p-2 rounded-xl shrink-0">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground pl-2 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> Branch:
            </span>
            <select
              value={selectedOfficeId}
              onChange={(e) => setSelectedOfficeId(e.target.value)}
              className="flex h-9 rounded-lg border border-input bg-background px-3 py-1 text-xs font-semibold text-foreground focus:outline-none"
              data-testid="office-filter-select"
            >
              <option value="">All Offices</option>
              {offices.map(o => (
                <option key={o.office_id || o.id} value={o.office_id || o.id}>{o.name || o.office_name} ({o.city})</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-muted/40 border border-border p-2.5 rounded-xl shrink-0 text-xs">
            <Building2 className="w-4 h-4 text-primary shrink-0" />
            <span className="font-semibold">
              {offices.find(o => String(o.office_id || o.id) === String(user?.office_id))?.name || "My Branch"} Office
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-6" aria-label="Loading CRM workspace">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((card) => <Skeleton key={card} className="h-24 rounded-xl" />)}
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <Skeleton className="h-96 rounded-2xl lg:col-span-2" />
            <Skeleton className="h-96 rounded-2xl" />
          </div>
        </div>
      ) : dataError ? (
        <Card className="border-destructive/30 bg-destructive/5 p-8 text-center" role="alert">
          <p className="font-bold text-foreground">Office CRM could not be loaded</p>
          <p className="mt-2 text-sm text-muted-foreground">{dataError}</p>
          <Button type="button" onClick={loadData} className="mt-4">Try again</Button>
        </Card>
      ) : (
        <>
          {/* Metrics Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* KPI card: animated gradient icon chip that lifts on hover */}
            <Card className="group p-4 bg-card border border-border flex items-center gap-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
              <div className="p-3 bg-gradient-to-br from-blue-500/15 to-blue-400/5 ring-1 ring-blue-500/15 rounded-xl text-blue-600 dark:text-blue-400 shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3 animate-gradient-pan">
                <ClipboardList className="w-5 h-5 group-hover:animate-icon-bob" />
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground block">Active Leads</span>
                <span className="text-2xl font-black text-foreground">{totalLeadsCount}</span>
              </div>
            </Card>

            <Card className="group p-4 bg-card border border-border flex items-center gap-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
              <div className="p-3 bg-gradient-to-br from-amber-500/15 to-amber-400/5 ring-1 ring-amber-500/15 rounded-xl text-amber-600 dark:text-amber-400 shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3 animate-gradient-pan">
                <UserPlus className="w-5 h-5 group-hover:animate-icon-bob" />
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground block">Unassigned</span>
                <span className="text-2xl font-black text-foreground">{unassignedLeadsCount}</span>
              </div>
            </Card>

            <Card className="group p-4 bg-card border border-border flex items-center gap-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
              <div className="p-3 bg-gradient-to-br from-purple-500/15 to-purple-400/5 ring-1 ring-purple-500/15 rounded-xl text-purple-600 dark:text-purple-400 shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3 animate-gradient-pan">
                <CalendarDays className="w-5 h-5 group-hover:animate-icon-bob" />
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground block">Pending Visits</span>
                <span className="text-2xl font-black text-foreground">{pendingVisitsCount}</span>
              </div>
            </Card>

            <Card className="group p-4 bg-card border border-border flex items-center gap-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
              <div className="p-3 bg-gradient-to-br from-emerald-500/15 to-emerald-400/5 ring-1 ring-emerald-500/15 rounded-xl text-emerald-600 dark:text-emerald-400 shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3 animate-gradient-pan">
                <UsersIcon className="w-5 h-5 group-hover:animate-icon-bob" />
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground block">Active Agents</span>
                <span className="text-2xl font-black text-foreground">{totalAgentsCount}</span>
              </div>
            </Card>
          </div>

          {/* Main Workspace Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[500px]">
            
            {/* Left Section (7 columns): Planner & Activities */}
            <div className="lg:col-span-8 flex flex-col space-y-4">
              
              {/* Tab Selector */}
              <div className="flex justify-between items-center bg-muted/30 p-1.5 rounded-xl border border-border/50">
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => setActiveTab("visits")}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                      activeTab === "visits" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Calendar className="w-3.5 h-3.5" /> Site Visits Planner
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("timeline")}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                      activeTab === "timeline" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="tab-lead-assignments"
                  >
                    <UserCheck className="w-3.5 h-3.5" /> Lead Assignment Control
                  </button>
                </div>

                <Button asChild size="sm" className="font-bold text-xs gap-1.5" data-testid="open-visit-queue-btn">
                  <Link to="/dashboard/office/site-visits" title="Open buyer-requested Site Visits">
                    <Calendar className="w-3.5 h-3.5" /> Open Visit Queue
                  </Link>
                </Button>
              </div>

              {/* Tab Content 1: Site Visits */}
              {activeTab === "visits" && (
                <div className="flex-1 bg-card border border-border rounded-xl p-5 space-y-4">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-primary border-b border-border pb-2">Upcoming Customer Inspections</h2>
                  
                  {filteredVisits.length === 0 ? (
                    <div className="text-center py-16 text-muted-foreground space-y-2">
                      <Calendar className="w-8 h-8 mx-auto text-muted-foreground/40" />
                      <p className="text-xs">No site visits currently scheduled.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredVisits.map((v) => {
                        const agent = agents.find(a => String(a.user_id || a.id) === String(v.agent_id));
                        const property = properties.find(p => String(p.id) === String(v.property_id));
                        return (
                          <div 
                            key={v.site_visit_id || v.id}
                            className="p-4 rounded-xl border border-border/80 bg-muted/10 hover:border-primary/40 transition flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs"
                          >
                            <div className="space-y-1.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className={v.status === "SCHEDULED" ? "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200" : "bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200"}>
                                  {v.status}
                                </Badge>
                                <span className="font-mono text-muted-foreground">
                                  {v.scheduled_at || v.visit_at ? new Date(v.scheduled_at || v.visit_at).toLocaleString("en-IN") : "Schedule unavailable"}
                                </span>
                              </div>
                              <div className="font-bold text-sm text-foreground">
                                Buyer: {v.buyer_name} ({v.buyer_phone})
                              </div>
                              <div className="flex items-center gap-1.5 text-muted-foreground">
                                <Building2 className="w-3.5 h-3.5 shrink-0" />
                                <span>Property: {property ? property.title : "Property unavailable"}</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-muted-foreground">
                                <UserCheck className="w-3.5 h-3.5 shrink-0 text-primary" />
                                <span>Conducted By: {agent ? agent.name : "Unassigned"}</span>
                              </div>
                              {v.note && (
                                <p className="text-muted-foreground italic bg-muted/40 p-2 rounded-lg mt-1 border border-border/30">
                                  "{v.note}"
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Tab Content 2: Lead Assignment Control */}
              {activeTab === "timeline" && (
                <div className="flex-1 space-y-4">
                  {/* Search and Filters Strip */}
                  <form onSubmit={(e) => { e.preventDefault(); loadAssignments(); }} className="flex flex-col sm:flex-row gap-3 items-center bg-card border border-border/80 p-4 rounded-xl shadow-sm">
                    <div className="relative flex-grow w-full">
                      <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder={isUserAdmin ? "Search by buyer, phone, email, or property…" : "Search by buyer or property…"}
                        value={assignmentSearch}
                        onChange={(e) => setAssignmentSearch(e.target.value)}
                        maxLength={200}
                        className="pl-9 text-xs"
                      />
                    </div>
                    <div className="flex items-center gap-2 shrink-0 self-start sm:self-center">
                      <input
                        type="checkbox"
                        id="show-all-assignments"
                        checked={assignmentShowAll}
                        onChange={(e) => setAssignmentShowAll(e.target.checked)}
                        className="w-4 h-4 rounded border-input bg-background text-primary"
                      />
                      <label htmlFor="show-all-assignments" className="text-xs font-semibold text-foreground cursor-pointer select-none">
                        Include Assigned Leads
                      </label>
                    </div>
                    <Button type="submit" size="sm" className="font-bold shrink-0 w-full sm:w-auto">
                      Search
                    </Button>
                  </form>

                  {assignmentError ? (
                    <Card className="p-6 text-center border-destructive/30 bg-destructive/5" role="alert">
                      <p className="text-sm font-bold text-foreground">Lead assignments could not be loaded.</p>
                      <p className="mt-1 text-xs text-muted-foreground">{assignmentError}</p>
                      <Button type="button" size="sm" variant="outline" className="mt-4" onClick={() => loadAssignments()}>
                        Retry
                      </Button>
                    </Card>
                  ) : assignmentLeads.length === 0 ? (
                    <Card className="p-8 text-center text-xs text-muted-foreground border-dashed border-2 bg-card">
                      No pending or unassigned leads matching filter.
                    </Card>
                  ) : (
                    <div className="grid gap-3" data-testid="assignments-list">
                      {assignmentLeads.map((lead) => {
                        const isProposed = lead.assignment_status === "PROPOSED";
                        const badgeKey = isProposed ? "PROPOSED" : (lead.assignment_status || "UNASSIGNED");
                        return (
                          <Card key={lead.lead_id} className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border border-border/80 hover:border-primary/30 transition shadow-sm"
                            data-testid={`assignment-card-${lead.lead_id}`}>
                            <div className="space-y-2 min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className={ASSIGNMENT_BADGES[badgeKey] || ASSIGNMENT_BADGES.UNASSIGNED}>{badgeKey}</Badge>
                                <span className="text-[10px] text-muted-foreground font-mono">Lead ID: {String(lead.lead_id).slice(-8).toUpperCase()}</span>
                              </div>
                              
                              {/* Property Details */}
                              {lead.property_id ? (
                                <Link
                                  to={`/properties/${lead.property_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-bold truncate text-sm text-primary hover:text-primary/80 hover:underline flex items-center gap-1.5 w-fit"
                                >
                                  <Home className="w-4 h-4 shrink-0 text-muted-foreground" />
                                  Property: {lead.property_title || lead.property_code || lead.property_id}
                                </Link>
                              ) : (
                                <div className="font-bold truncate text-sm text-muted-foreground flex items-center gap-1.5">
                                  <Home className="w-4 h-4 shrink-0" />
                                  Plots on Demand (No Listing)
                                </div>
                              )}

                              {/* Buyer Metadata */}
                              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                                <UsersIcon className="w-4 h-4 shrink-0 text-muted-foreground" />
                                <span>Buyer: <strong className="text-foreground">{lead.buyer_name || "—"}</strong></span>
                                <span>· {lead.buyer_phone || "—"}</span>
                                <span>· {lead.buyer_email || "—"}</span>
                              </div>

                              {/* Agent Assignment Info */}
                              <div className="text-xs flex items-center gap-1.5">
                                <UserCheck className="w-4 h-4 shrink-0 text-primary" />
                                {lead.proposed_agent_name ? (
                                  <span>
                                    {lead.assignment_status === "APPROVED" 
                                      ? "Assigned agent: " 
                                      : lead.assignment_status === "REJECTED"
                                      ? "Rejected agent: "
                                      : "Proposed agent: "}
                                    <strong className="text-foreground">{lead.proposed_agent_name}</strong>
                                    {lead.proposed_agent_phone && ` (${lead.proposed_agent_phone})`}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground italic">No agent assigned</span>
                                )}
                              </div>
                              
                              {lead.proposed_note && (
                                <div className="text-xs italic text-muted-foreground bg-muted/30 p-2 rounded-lg border border-border/20 max-w-xl">
                                  "{lead.proposed_note}"
                                </div>
                              )}
                            </div>
                            
                            <div className="flex gap-2 shrink-0">
                              {isProposed && isUserAdmin ? (
                                <>
                                  <Button size="sm" className="gap-2 font-bold" disabled={!!busy}
                                    onClick={() => handleApproveAssignment(lead)} data-testid={`btn-approve-${lead.lead_id}`}>
                                    <CheckCircle2 className="w-3.5 h-3.5"/> Approve
                                  </Button>
                                  <Button size="sm" variant="outline" className="gap-2 font-bold text-destructive hover:bg-destructive/5" disabled={!!busy}
                                    onClick={() => { setRejectReason(""); setRejectDialog({ open: true, lead }); }} data-testid={`btn-reject-${lead.lead_id}`}>
                                    <XCircle className="w-3.5 h-3.5"/> Reject
                                  </Button>
                                </>
                              ) : isProposed ? (
                                <span className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs font-bold text-amber-700">
                                  Awaiting admin approval
                                </span>
                              ) : (
                                <Button size="sm" className="gap-2 font-bold" disabled={!!busy}
                                  onClick={() => openAssignLead(lead)} data-testid={`btn-assign-${lead.lead_id}`}>
                                  <UserPlus className="w-3.5 h-3.5"/> 
                                  {lead.assignment_status === "APPROVED" ? "Change Agent" : "Assign Agent"}
                                </Button>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Section (4 columns): Leaderboard & Direct Delegation */}
            <div className="lg:col-span-4 space-y-4">
              <Card className="border border-border p-5 bg-card flex flex-col h-full space-y-4">
                <div>
                  <h3 className="font-display font-bold text-lg text-primary flex items-center gap-1.5">
                    <TrendingUp className="w-4 h-4 text-emerald-500" /> Agent CRM Leaderboard
                  </h3>
                  <p className="text-muted-foreground text-xs mt-0.5">Manage agents, monitor active workloads, and delegate leads.</p>
                </div>

                <div className="flex-1 overflow-y-auto space-y-3 pr-1 max-h-[450px]">
                  {filteredAgents.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground italic text-xs">
                      No active agents registered in this branch.
                    </div>
                  ) : (
                    filteredAgents.map(a => {
                      const agentId = a.user_id || a.id;
                      const agentLeads = leads.filter(l => String(l.assigned_agent_id) === String(agentId));
                      const agentVisits = siteVisits.filter(v => String(v.agent_id) === String(agentId));
                      
                      return (
                        <div 
                          key={agentId} 
                          className="p-3.5 rounded-xl border border-border/80 bg-muted/10 hover:border-primary/30 transition text-xs space-y-2.5"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-bold text-foreground text-sm">{a.name}</h4>
                              <p className="text-muted-foreground text-[10px] font-mono">{a.email}</p>
                            </div>
                            <Button 
                              size="xs" 
                              variant="outline" 
                              className="font-bold text-[10px] text-primary"
                              onClick={() => setAssignDialog({ open: true, agent: a })}
                              data-testid={`btn-assign-lead-${agentId}`}
                            >
                              Assign Lead
                            </Button>
                          </div>

                          <div className="grid grid-cols-2 gap-2 pt-1 border-t border-border/30 text-[10px]">
                            <div className="bg-background p-1.5 rounded border border-border/40 text-center">
                              <span className="text-muted-foreground block">Active Leads</span>
                              <span className="text-sm font-bold text-primary">{agentLeads.length}</span>
                            </div>
                            <div className="bg-background p-1.5 rounded border border-border/40 text-center">
                              <span className="text-muted-foreground block">Site Visits</span>
                              <span className="text-sm font-bold text-purple-600">{agentVisits.length}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </Card>
            </div>

          </div>
        </>
      )}

      {/* QUICK DELEGATE/ASSIGN LEAD DIALOG */}
      <Dialog open={assignDialog.open} onOpenChange={(v) => !v && setAssignDialog({ open: false, agent: null })}>
        <DialogContent className="sm:max-w-md" data-testid="quick-assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight uppercase text-primary">
              Assign Lead to {assignDialog.agent?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleQuickAssign} className="space-y-4">
            
            {/* Select Unassigned Lead */}
            <div className="space-y-1.5">
              <Label htmlFor="assign-lead" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Select Unassigned Lead *</Label>
              <select
                id="assign-lead"
                value={assignLeadId}
                onChange={(e) => setAssignLeadId(e.target.value)}
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="select-assign-lead"
              >
                <option value="">-- Select a Lead --</option>
                {filteredLeads.filter(l => !l.assigned_agent_id).length === 0 ? (
                  <option disabled value="">No unassigned leads available for this branch</option>
                ) : (
                  filteredLeads.filter(l => !l.assigned_agent_id).map(l => (
                    <option key={l.lead_id || l.id} value={l.lead_id || l.id}>
                      {l.buyer_name || l.name || "Client"} - {l.is_plot_demand ? "Plots on Demand" : l.property_code || "General Enquiry"}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Note / Message */}
            <div className="space-y-1.5">
              <Label htmlFor="assign-note" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Instruction Note</Label>
              <Textarea
                id="assign-note"
                rows={3}
                value={assignNote}
                onChange={(e) => setAssignNote(e.target.value)}
                placeholder="e.g. Please follow up on this plot requirement urgently."
                data-testid="input-assign-note"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setAssignDialog({ open: false, agent: null })}>Cancel</Button>
              <Button type="submit" disabled={busy === "assigning"} className="font-bold">
                {busy === "assigning" ? "Assigning..." : isUserAdmin ? "Assign Directly" : "Propose Assignment"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* DIRECT ASSIGN AGENT DIALOG */}
      <Dialog open={assignLeadDialog.open} onOpenChange={(v) => !v && setAssignLeadDialog({ open: false, lead: null })}>
        <DialogContent className="sm:max-w-md" data-testid="assign-lead-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight uppercase text-primary">
              Assign Agent to Lead
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Select Agent *</Label>
              <Select value={assignAgentId} onValueChange={setAssignAgentId}>
                <SelectTrigger data-testid="select-assign-lead-agent">
                  <SelectValue placeholder="Pick an agent" />
                </SelectTrigger>
                <SelectContent>
                  {filteredAgents.map((a) => (
                    <SelectItem key={a.user_id || a.id} value={a.user_id || a.id}>
                      {a.name} · {a.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={() => setAssignLeadDialog({ open: false, lead: null })}>Cancel</Button>
              <Button onClick={handleAssignLeadSubmit} disabled={!assignAgentId || busy === "assigning-lead"} className="font-bold" data-testid="btn-submit-assign-lead">
                {busy === "assigning-lead" ? "Assigning..." : isUserAdmin ? "Assign Directly" : "Propose Assignment"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Accessible rejection flow replaces the browser prompt. */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => {
        if (!open && !busy) {
          setRejectDialog({ open: false, lead: null });
          setRejectReason("");
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="office-reject-assignment-dialog">
          <DialogHeader>
            <DialogTitle>Reject this assignment proposal?</DialogTitle>
            <DialogDescription>
              Add the operational reason so the proposal can be corrected and resubmitted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="office-assignment-rejection-reason">Rejection reason *</Label>
            <Textarea
              id="office-assignment-rejection-reason"
              value={rejectReason}
              onChange={(event) => setRejectReason(event.target.value)}
              maxLength={1000}
              rows={4}
              placeholder="Explain why this assignment is not suitable"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={!!busy} onClick={() => {
              setRejectDialog({ open: false, lead: null });
              setRejectReason("");
            }}>Keep proposal</Button>
            <Button type="button" variant="destructive" disabled={!!busy || !rejectReason.trim()} onClick={handleRejectAssignment}>
              {busy ? "Rejecting…" : "Reject proposal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
